import React, { useEffect, useState } from "react";
import { Button, Header, Icon, Loader, Message } from "semantic-ui-react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";

import { getDriverConversations } from "api/backendAPI";

import "./DriverConversations.scss";

const CONTRACT_VERSION = "1.0.0";
const HEX_REF = /^[a-f0-9]{64}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isDate(value) {
  return typeof value === "string" && !Number.isNaN(new Date(value).getTime());
}

export function validateConversationResponse(response, requestedDay) {
  if (!response || response.day !== requestedDay || response.timezone !== "Europe/Helsinki" || !Array.isArray(response.items)) return false;
  return response.items.every(thread => thread
    && HEX_REF.test(thread.conversation_ref)
    && UUID.test(thread.driver_id)
    && typeof thread.driver_name === "string"
    && thread.driver_name.trim().length > 0
    && typeof thread.primary_language === "string"
    && isDate(thread.opened_at)
    && isDate(thread.last_message_at)
    && Array.isArray(thread.messages)
    && thread.messages.length > 0
    && thread.messages.every(message => message
      && HEX_REF.test(message.message_ref)
      && (message.speaker === "driver" || message.speaker === "aimo")
      && typeof message.original_text === "string"
      && message.original_text.trim().length > 0
      && typeof message.finnish_text === "string"
      && message.finnish_text.trim().length > 0
      && isDate(message.occurred_at)
      && (!message.delivery || (message.speaker === "aimo" && ["accepted", "sent", "delivered", "read", "failed"].includes(message.delivery.status) && isDate(message.delivery.occurred_at)))
      && (!message.reaction || (message.speaker === "driver" && message.reaction.emoji === "👍" && ["accepted", "sent", "delivered", "read", "failed", "uncertain"].includes(message.reaction.status) && isDate(message.reaction.occurred_at)))));
}

function timeOnly(value) {
  return new Intl.DateTimeFormat("fi-FI", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Helsinki" }).format(new Date(value));
}

function speakerLabel(speaker, t) {
  return speaker === "aimo" ? t("driverConversations.aimo") : t("driverConversations.driver");
}

function visibleMessages(messages) {
  return messages.filter(message => !(message.speaker === "aimo" && /^NO_REPLY$/i.test(message.original_text.trim())))
    .sort((a, b) => new Date(a.occurred_at) - new Date(b.occurred_at));
}

export function newestConversations(items) {
  return items.map(thread => {
    const messages = visibleMessages(thread.messages);
    return { ...thread, messages, last_message_at: messages.length ? messages[messages.length - 1].occurred_at : null };
  }).filter(thread => thread.messages.length > 0)
    .sort((a, b) => new Date(b.last_message_at) - new Date(a.last_message_at));
}

export function ConversationThread({ thread, t }) {
  const [expanded, setExpanded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(6);
  const displayName = thread.preferred_name || thread.driver_name;
  const messages = visibleMessages(thread.messages);
  if (!messages.length) return null;
  const latest = messages[messages.length - 1];
  const hiddenCount = Math.max(0, messages.length - visibleCount);
  const bodyId = `conversation-${thread.conversation_ref}`;
  return (
    <article className="conversationThread" data-testid="conversation-thread">
      <h2 className="conversationThread__heading">
        <button className="conversationThread__summary" type="button" aria-expanded={expanded} aria-controls={bodyId} onClick={() => setExpanded(value => !value)}>
          <span className="conversationThread__avatar" aria-hidden="true">{displayName.charAt(0)}</span>
          <span className="conversationThread__overview">
            <span className="conversationThread__title"><strong>{displayName}</strong><time dateTime={latest.occurred_at}>{timeOnly(latest.occurred_at)}</time></span>
            <span className="conversationThread__preview">{speakerLabel(latest.speaker, t)}: {latest.finnish_text}</span>
            <span className="conversationThread__facts">{t("driverConversations.messageCount", { count: messages.length })} · {thread.primary_language.toUpperCase()} · {t(expanded ? "driverConversations.closeThread" : "driverConversations.openThread")}</span>
          </span>
          <Icon name={expanded ? "chevron up" : "chevron down"} />
        </button>
      </h2>
      <div id={bodyId} hidden={!expanded}>
        {expanded && <div className="conversationThread__body">
          <p className="conversationThread__participants">{thread.driver_name} · {t("driverConversations.aimo")}</p>
          {hiddenCount > 0 && <button type="button" className="conversationThread__earlier" onClick={() => setVisibleCount(count => count + 20)}>{t("driverConversations.showEarlier", { count: hiddenCount })}</button>}
          <ol className="conversationThread__messages">
            {messages.slice(-visibleCount).map(message => (
              <li className={`conversationMessage conversationMessage--${message.speaker}`} key={message.message_ref}>
                <div className="conversationMessage__meta">
                  <strong>{message.speaker === "aimo" ? t("driverConversations.aimo") : displayName}</strong>
                  <time dateTime={message.occurred_at}>{timeOnly(message.occurred_at)}</time>
                </div>
                <p lang={thread.primary_language}>{message.original_text}</p>
                {message.speaker === "aimo" && <small className="conversationMessage__delivery">
                  {t(`driverConversations.delivery.${message.delivery ? message.delivery.status : "unknown"}`)}
                  {message.delivery && <> · <time dateTime={message.delivery.occurred_at}>{timeOnly(message.delivery.occurred_at)}</time></>}
                </small>}
                {message.reaction && <span className="conversationMessage__reaction" role="img"
                  aria-label={t(`driverConversations.reaction.${message.reaction.status}`)}
                  title={t(`driverConversations.reaction.${message.reaction.status}`)}>
                  {["failed", "uncertain"].includes(message.reaction.status) ? t(`driverConversations.reaction.${message.reaction.status}`) : <span aria-hidden="true">👍</span>}
                </span>}
                {message.original_text.trim() !== message.finnish_text.trim() && <details className="conversationMessage__translation">
                  <summary>{t("driverConversations.finnish")}</summary>
                  <p lang="fi">{message.finnish_text}</p>
                </details>}
              </li>
            ))}
          </ol>
        </div>}
      </div>
    </article>
  );
}

export default function DriverConversations() {
  const { t } = useTranslation();
  const [day, setDay] = useState("today");
  const [reloadKey, setReloadKey] = useState(0);
  const [state, setState] = useState({ loading: true, error: null, items: [], lastLoaded: null });

  useEffect(() => {
    let active = true;
    setState(previous => ({ ...previous, loading: true, error: null }));
    getDriverConversations(day)
      .then(response => {
        if (response.conversationContractVersion !== CONTRACT_VERSION || !validateConversationResponse(response.data, day)) {
          throw new Error("contract");
        }
        if (active) setState({ loading: false, error: null, items: newestConversations(response.data.items), lastLoaded: new Date().toISOString() });
      })
      .catch(error => {
        if (active) setState(previous => ({ ...previous, loading: false, error: error.message === "contract" ? "contract" : "network", items: [] }));
      });
    return () => { active = false; };
  }, [day, reloadKey]);

  return (
    <main className="pageShell driverConversations">
      <Helmet title={t("driverConversations.title")} />
      <div className="pageHeader">
        <div>
          <p className="pageHeader__eyebrow">FleetAdmin TMS · {t("navbar.readOnly")}</p>
          <Header as="h1">{t("driverConversations.title")}</Header>
          <p className="pageHeader__lead">{t("driverConversations.lead")}</p>
        </div>
        <Button className="refreshButton" onClick={() => setReloadKey(value => value + 1)} icon="refresh" content={t("driverConversations.refresh")} />
      </div>
      <section className="conversationTools" aria-label={t("driverConversations.daySelection")}>
        <div className="conversationTools__days">
          {["today", "yesterday"].map(value => (
            <button className={day === value ? "is-active" : ""} key={value} type="button" aria-pressed={day === value} onClick={() => { if (value !== day) { setDay(value); setState(previous => ({ ...previous, items: [], lastLoaded: null })); } }}>
              {t(`driverConversations.${value}`)}
            </button>
          ))}
        </div>
        <p><Icon name="shield alternate" />{t("driverConversations.privacyNote")}</p>
      </section>
      {state.loading && <Loader active inline="centered" content={t("driverConversations.loading")} />}
      {!state.loading && state.error && <Message negative header={t("driverConversations.errorTitle")} content={t(`driverConversations.errors.${state.error}`)} />}
      {!state.loading && !state.error && state.items.length === 0 && (
        <Message icon><Icon name="comments outline" /><Message.Content><Message.Header>{t("driverConversations.emptyTitle")}</Message.Header>{t("driverConversations.emptyBody")}</Message.Content></Message>
      )}
      {state.items.length > 0 && <p className="driverConversations__order">{t("driverConversations.newestFirst")}</p>}
      {!state.error && state.items.map(thread => <ConversationThread key={`${day}-${thread.conversation_ref}`} thread={thread} t={t} />)}
      {state.lastLoaded && <p className="driverConversations__loadedAt">{t("driverConversations.lastLoaded")}: {timeOnly(state.lastLoaded)}</p>}
    </main>
  );
}
